import { useState, useEffect } from "react";
import axiosInstance from "../../../api/axiosConfig";
import * as salesLocationApi from "../../../api/salesLocationApi";

export const useDashboardData = () => {
  const [provinceStats, setProvinceStats] = useState([]);
  const [cantonStats, setCantonStats] = useState({
    Pichincha: [],
    Guayas: [],
  });
  // Datos específicos para nocturno
  const [nightCantonStats, setNightCantonStats] = useState({
    Pichincha: [],
    Guayas: [],
  });
  const [globalStats, setGlobalStats] = useState({
    Pichincha: { avgDayDistance: 0, avgNightDistance: 0 },
    Guayas: { avgDayDistance: 0, avgNightDistance: 0 },
  });
  const [parishStats, setParishStats] = useState({
    Pichincha: {},
    Guayas: {},
  });
  const [parishDealerStats, setParishDealerStats] = useState({
    diurno: {},
    nocturno: {},
  });
  const [parishDealerDistances, setParishDealerDistances] = useState({
    diurno: {},
    nocturno: {},
  });
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [
          provinceResponse,
          pichincha,
          guayas,
          pichinchanocturno,
          guayasnocturno,
          agenciesResponse,
          parishDealerDiurnoResponse,
          parishDealerNocturnoResponse,
          globalStatsResponse,
          parishDealerDistancesDiurnoResponse,
          parishDealerDistancesNocturnoResponse,
        ] = await Promise.all([
          salesLocationApi.getProvinceStats(),
          salesLocationApi.getCantonStats("Pichincha"),
          salesLocationApi.getCantonStats("Guayas"),
          salesLocationApi.getCantonStatsNocturno("Pichincha"),
          salesLocationApi.getCantonStatsNocturno("Guayas"),
          axiosInstance.get("/agencies"),
          salesLocationApi.getParishDealersStatsDiurno(),
          salesLocationApi.getParishDealersStatsNocturno(),
          salesLocationApi.getGlobalStats(),
          salesLocationApi.getParishDealerDistancesDiurno(),
          salesLocationApi.getParishDealerDistancesNocturno(),
        ]);

        // Obtener datos de parroquias para cada cantón
        const pichinchaParishes = await Promise.all(
          pichincha.data.map(async (canton) => {
            const response = await salesLocationApi.getParishStats(
              "Pichincha",
              canton.day_canton
            );
            return { canton: canton.day_canton, parishes: response.data };
          })
        );

        const guayasParishes = await Promise.all(
          guayas.data.map(async (canton) => {
            const response = await salesLocationApi.getParishStats(
              "Guayas",
              canton.day_canton
            );
            return { canton: canton.day_canton, parishes: response.data };
          })
        );

        // Organizar los datos de parroquias por cantón
        const pichinchaParishStats = {};
        pichinchaParishes.forEach(({ canton, parishes }) => {
          pichinchaParishStats[canton] = parishes;
        });

        const guayasParishStats = {};
        guayasParishes.forEach(({ canton, parishes }) => {
          guayasParishStats[canton] = parishes;
        });

        if (!provinceResponse.data || !pichincha.data || !guayas.data) {
          throw new Error("Datos incompletos recibidos del servidor");
        }

        // Procesar los datos de distancia y fusionarlos con los datos de parroquia-dealer
        const processedParishDealerStatsDiurno = {
          ...parishDealerDiurnoResponse.data,
        };
        const processedParishDealerStatsNocturno = {
          ...parishDealerNocturnoResponse.data,
        };

        // Añadir datos de distancia promedio a cada dealer en cada parroquia
        if (parishDealerDistancesDiurnoResponse.data) {
          Object.entries(parishDealerDistancesDiurnoResponse.data).forEach(
            ([parishDealerKey, distanceData]) => {
              if (processedParishDealerStatsDiurno[parishDealerKey]) {
                processedParishDealerStatsDiurno[parishDealerKey].dealers =
                  processedParishDealerStatsDiurno[parishDealerKey].dealers.map(
                    (dealer) => {
                      const dealerDistance = distanceData.find(
                        (d) =>
                          d.dealerId === dealer.id ||
                          d.dealerName === dealer.name
                      );
                      return {
                        ...dealer,
                        avgDistance: dealerDistance
                          ? dealerDistance.avgDistance
                          : undefined,
                      };
                    }
                  );
              }
            }
          );
        }

        if (parishDealerDistancesNocturnoResponse.data) {
          Object.entries(parishDealerDistancesNocturnoResponse.data).forEach(
            ([parishDealerKey, distanceData]) => {
              if (processedParishDealerStatsNocturno[parishDealerKey]) {
                processedParishDealerStatsNocturno[parishDealerKey].dealers =
                  processedParishDealerStatsNocturno[
                    parishDealerKey
                  ].dealers.map((dealer) => {
                    const dealerDistance = distanceData.find(
                      (d) =>
                        d.dealerId === dealer.id || d.dealerName === dealer.name
                    );
                    return {
                      ...dealer,
                      avgDistance: dealerDistance
                        ? dealerDistance.avgDistance
                        : undefined,
                    };
                  });
              }
            }
          );
        }

        setProvinceStats(provinceResponse.data);
        setCantonStats({
          Pichincha: pichincha.data,
          Guayas: guayas.data,
        });
        setNightCantonStats({
          Pichincha: pichinchanocturno.data || [],
          Guayas: guayasnocturno.data || [],
        });
        setParishStats({
          Pichincha: pichinchaParishStats,
          Guayas: guayasParishStats,
        });
        setAgencies(agenciesResponse.data || []);
        setGlobalStats(globalStatsResponse.data);
        setParishDealerStats({
          diurno: processedParishDealerStatsDiurno,
          nocturno: processedParishDealerStatsNocturno,
        });
        setParishDealerDistances({
          diurno: parishDealerDistancesDiurnoResponse.data || {},
          nocturno: parishDealerDistancesNocturnoResponse.data || {},
        });
      } catch (error) {
        console.error("Error al obtener datos:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error al cargar los datos";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    provinceStats,
    cantonStats,
    dayCantonStats: cantonStats, // Datos diurnos
    nightCantonStats, // Datos nocturnos reales
    globalStats,
    parishStats,
    parishDealerStats,
    parishDealerDistances,
    agencies,
    loading,
    error,
  };
};

export default useDashboardData;
